import { createApi } from '@reduxjs/toolkit/query/react';
import { endPoints, urlKeys } from 'src/api/APIEndpoints';
import { IRadioInfo } from 'src/types/FormControlRadioGroupProps';
import { ISiteStudyPersonalResponse } from 'src/types/SiteStudyPersonal';
import { KeyValue, formatString } from 'src/util/STR';
import { tsdaBaseQuery } from './baseQuery';

export const getDefaultOptions = (): KeyValue[] => [
  { key: 'yes', value: 'Y' },
  { key: 'no', value: 'N' }
];

export const getOptions = (...args: KeyValue[]): IRadioInfo[] => {
  return args.map((arg): IRadioInfo => {
    return { value: arg.value, labelkey: arg.key };
  });
};

export const getResponseWithDefaultOptions = (data: ISiteStudyPersonalResponse): ISiteStudyPersonalResponse => {
  data.study_id_enabled = data.site.identified_for_study.trim().toLowerCase() === 'y';
  data.site.study_id = data.study.study_id;
  data.personnel.forEach(personel => (personel.study_id = data.study.study_id));
  data.options = getOptions(...getDefaultOptions());
  return data;
};
export const tsdaIntegrationAPI = createApi({
  baseQuery: tsdaBaseQuery,
  reducerPath: 'TSDAIntegrationAPI',
  endpoints: builder => ({
    getTsdaSiteStudyPersonnelData: builder.query<any, any>({
      query: ({ site_id, study_id, country, application }) => ({
        url: `${formatString(
          endPoints.SiteStudyPersonnel.siteStudyPersonnelSelect,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&site_contact_info=true`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getTsdaSiteStudyPersonnels: builder.query<any, any>({
      query: ({ site_id, study_id, country, application }) => ({
        url: `${formatString(
          endPoints.SiteStudyPersonnel.personnelSelect,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&site_contact_info=true`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getTsdaStudyInfo: builder.query<any, any>({
      query: ({ site_id, study_id, country, application }) => ({
        url: `${formatString(endPoints.DrugAccountabilityRecord.getStudyInfo, {
          key: urlKeys.study_id,
          value: study_id
        })}?site_id=${site_id}&country=${country}&application=${application}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getTsdaSiteStudyPersonnel: builder.query<
      ISiteStudyPersonalResponse,
      { site_id: any; study_id: any; country: any; application: any }
    >({
      query: ({ site_id, study_id, country, application }) => ({
        url: formatString(
          endPoints.SiteStudyPersonnel.siteStudyPersonnelSelect,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application }
        )
      }),
      transformResponse: (response: ISiteStudyPersonalResponse) => {
        return getResponseWithDefaultOptions(response);
      }
    }),
    getTsdaSiteStudyPersonnelCopy: builder.query<
      ISiteStudyPersonalResponse,
      { site_id: any; study_id: any; country: any; application: any }
    >({
      query: ({ site_id, study_id, country, application }) => ({
        url: formatString(
          endPoints.SiteStudyPersonnel.siteStudyPersonnelSelect,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application }
        )
      })
    })
  })
});

export const {
  useLazyGetTsdaSiteStudyPersonnelDataQuery,
  useGetTsdaSiteStudyPersonnelsQuery,
  useGetTsdaStudyInfoQuery,
  useGetTsdaSiteStudyPersonnelDataQuery,
  useLazyGetTsdaSiteStudyPersonnelCopyQuery
} = tsdaIntegrationAPI;
